import React from 'react';
import { Box, Typography, Chip, Button, Tooltip } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PersonIcon from '@mui/icons-material/Person';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import { useMsal } from '@azure/msal-react';
import NewsCategoryIcon from './NewsCategoryIcon';
import { PUBLISH_STATES } from '../api/NewsQueueApiConstants';
import {
    NEWS_NEW_BADGE_WINDOW_MS,
    NEWS_NEW_HIGHLIGHT_FADE_MS,
    toUiPublishState,
    UI_PUBLISH_STATES,
} from './NewsQueueConstants';

const UI_STATE_LABEL_MESSAGE_ID = {
    [UI_PUBLISH_STATES.PENDING]: 'newsQueue.state.new',
    [UI_PUBLISH_STATES.ACCEPTED]: 'newsQueue.state.published',
    [UI_PUBLISH_STATES.REJECTED]: 'newsQueue.state.rejected',
};

function getArticleEditsStorageKey(articleId) {
    return `newsQueue.articleEdits.${articleId}`;
}

function ListArticle({
    article,
    lock,
    newlyInsertedAt,
    onClick,
    onCategoryPick,
    onStatusPick,
    onRejectClick,
    isRejectPending = false,
    configurations,
}) {
    const intl = useIntl();
    const theme = useTheme();
    const msal = useMsal();
    const [editsVersion, setEditsVersion] = React.useState(0);
    const [isNewBadgeVisible, setIsNewBadgeVisible] = React.useState(false);
    const [isHighlightVisible, setIsHighlightVisible] = React.useState(false);

    React.useEffect(() => {
        const handleLocalEditsChanged = (event) => {
            const changedArticleId = event?.detail?.articleId;
            if (!changedArticleId || changedArticleId === article?.id) {
                setEditsVersion((v) => v + 1);
            }
        };

        const handleStorage = () => setEditsVersion((v) => v + 1);

        window.addEventListener('newsQueue:articleEditsChanged', handleLocalEditsChanged);
        window.addEventListener('storage', handleStorage);
        return () => {
            window.removeEventListener('newsQueue:articleEditsChanged', handleLocalEditsChanged);
            window.removeEventListener('storage', handleStorage);
        };
    }, [article?.id]);

    const articleEdits = React.useMemo(() => {
        const articleId = article?.id;
        if (!articleId) return {};
        try {
            const raw = localStorage.getItem(getArticleEditsStorageKey(articleId));
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    }, [article?.id, editsVersion]);

    const effectiveArticleHeader = typeof articleEdits?.article_header === 'string'
        ? articleEdits.article_header
        : article?.article_header;

    const effectiveArticleIngress = typeof articleEdits?.article_ingress === 'string'
        ? articleEdits.article_ingress
        : article?.article_ingress;

    const hasHeaderEdit =
        typeof articleEdits?.article_header === 'string' &&
        articleEdits.article_header !== String(article?.article_header ?? '');

    const hasIngressEdit =
        typeof articleEdits?.article_ingress === 'string' &&
        articleEdits.article_ingress !== String(article?.article_ingress ?? '');

    const hasBodyEdit =
        typeof articleEdits?.article_body === 'string' &&
        articleEdits.article_body !== String(article?.article_body ?? '');

    const hasCaptionEdit =
        typeof articleEdits?.caption === 'string' &&
        articleEdits.caption !== String(article?.caption ?? '');

    const hasPhotographerEdit =
        typeof articleEdits?.photographer === 'string' &&
        articleEdits.photographer !== String(article?.photographer ?? '');

    const normalizeImageValue = (value) => {
        const text = String(value ?? '').trim();
        return text.length > 0 ? text : '';
    };

    const hasImageEdit =
        Object.prototype.hasOwnProperty.call(articleEdits ?? {}, 'image_data') &&
        normalizeImageValue(articleEdits.image_data) !== normalizeImageValue(article?.image_data);

    const hasActiveEdits =
        hasHeaderEdit ||
        hasIngressEdit ||
        hasBodyEdit ||
        hasCaptionEdit ||
        hasPhotographerEdit ||
        hasImageEdit;

    const publishState = toUiPublishState(article?.publish_state);
    const canEditFields =
        publishState !== UI_PUBLISH_STATES.ACCEPTED &&
        publishState !== UI_PUBLISH_STATES.REJECTED;

    const category = article?.category;
    const categoryLabel = category
        ? category.charAt(0).toUpperCase() + category.slice(1)
        : '';

    const targetMediaText = (() => {
        const v = article?.target_media;
        if (!v) return '';
        if (Array.isArray(v)) {
            return v
                .filter(Boolean)
                .map((vv) => {
                    const key = String(vv).trim();
                    return configurations?.media_name_by_target_media?.[key] ?? key;
                })
                .join(', ');
        }
        const key = String(v).trim();
        return configurations?.media_name_by_target_media?.[key] ?? key;
    })();

    const publishedLabel = publishState
        ? intl.formatMessage({ id: UI_STATE_LABEL_MESSAGE_ID[publishState] ?? 'newsQueue.state.unknown' })
        : intl.formatMessage({ id: 'newsQueue.state.unknown' });

    const publishedColor =
        publishState === UI_PUBLISH_STATES.REJECTED
            ? 'error'
            : publishState === UI_PUBLISH_STATES.ACCEPTED
                ? 'primary'
                : 'default';

    const ts = new Date(article.timestamp_article ?? article.timestamp_created);
    const timeStr = ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

    const activeAccount = msal?.instance?.getActiveAccount?.();
    const currentUserId =
        activeAccount?.username ?? activeAccount?.name ?? activeAccount?.localAccountId ?? null;

    const isLockActive = (() => {
        const expiresAt = Date.parse(String(lock?.lock_expires_at ?? ''));
        return !Number.isNaN(expiresAt) && expiresAt > Date.now();
    })();

    const lockOwner = String(lock?.locked_by ?? '').trim();
    const isLocked = isLockActive && lockOwner.length > 0;
    const isLockedByOtherUser =
        isLocked &&
        String(currentUserId ?? '').trim().toLowerCase() !== lockOwner.toLowerCase();

    React.useEffect(() => {
        const createdTs = Date.parse(String(article?.timestamp_created ?? ''));
        if (Number.isNaN(createdTs)) {
            setIsNewBadgeVisible(false);
            return;
        }

        const age = Date.now() - createdTs;
        if (age >= NEWS_NEW_BADGE_WINDOW_MS) {
            setIsNewBadgeVisible(false);
            return;
        }

        setIsNewBadgeVisible(true);
        const hideInMs = NEWS_NEW_BADGE_WINDOW_MS - age;
        const timer = setTimeout(() => setIsNewBadgeVisible(false), hideInMs);
        return () => clearTimeout(timer);
    }, [article?.timestamp_created]);

    React.useLayoutEffect(() => {
        if (!newlyInsertedAt) {
            setIsHighlightVisible(false);
            return;
        }

        setIsHighlightVisible(true);

        let frameId2 = null;
        const frameId1 = window.requestAnimationFrame(() => {
            frameId2 = window.requestAnimationFrame(() => {
                setIsHighlightVisible(false);
            });
        });

        return () => {
            window.cancelAnimationFrame(frameId1);
            if (frameId2 != null) {
                window.cancelAnimationFrame(frameId2);
            }
        };
    }, [newlyInsertedAt]);

    return (
        <Box
            sx={{
                borderRadius: 1,
                backgroundColor: isHighlightVisible ? 'success.light' : 'transparent',
                transition: `background-color ${NEWS_NEW_HIGHLIGHT_FADE_MS}ms linear`,
                overflow: 'hidden',
            }}
        >
            <Box
                onClick={() => onClick(article)}
                sx={{
                    cursor: 'pointer',
                    py: 2,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: 'action.hover' },
                    display: 'grid',
                    gridTemplateColumns: '120px 60px minmax(0, 1fr) auto',
                    columnGap: 2,
                    rowGap: 0.5,
                    alignItems: 'start',
                }}
            >
                <Box
                    sx={{
                        gridColumn: 1,
                        width: '120px',
                        gridRow: '1 / span 2',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        justifyContent: 'flex-start',
                    }}
                >
                    <Box>
                        <Chip
                            icon={<NewsCategoryIcon
                                category={category}
                                fontSize="small"
                                color="primary"
                                style={{ paddingRight: 6, paddingLeft: "-5px" }} />}
                            label={categoryLabel}
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onCategoryPick && onCategoryPick(category);
                            }}
                            sx={{ width: '100%', '& .MuiChip-label': { fontSize: 11, px: 0, textOverflow: 'ellipsis' } }}
                        />

                        {targetMediaText && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 1.25, display: 'block', textAlign: 'center', lineHeight: 1.2 }}
                            >
                                {targetMediaText}
                            </Typography>
                        )}
                    </Box>

                    {isNewBadgeVisible || (canEditFields && hasActiveEdits) || isLocked ? (
                        <Box
                            sx={{
                                mt: 0.75,
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: 0.5,
                            }}
                        >
                            {isNewBadgeVisible && (
                                <Chip
                                    label="Uusi"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        borderColor: 'success.main',
                                        color: 'success.main',
                                        width: 'fit-content',
                                    }}
                                />
                            )}

                            {canEditFields && hasActiveEdits && (
                                <EditNoteIcon fontSize="small" color="action" />
                            )}
                            {isLocked && (
                                <PersonIcon
                                    fontSize="small"
                                    color={isLockedByOtherUser ? 'error' : 'action'}
                                />
                            )}
                        </Box>
                    ) : null}
                </Box>

                <Box sx={{ gridColumn: 2, gridRow: 1, textAlign: 'center' }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                        {timeStr}
                    </Typography>
                </Box>

                <Box sx={{ gridColumn: 3, gridRow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'left' }}>
                        {effectiveArticleHeader}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        gridColumn: 4,
                        gridRow: '1 / span 2',
                        justifySelf: 'end',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box>
                        {publishState && publishState !== UI_PUBLISH_STATES.PENDING && (
                            <Chip
                                label={publishedLabel}
                                size="small"
                                variant="outlined"
                                color={publishedColor}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onStatusPick && onStatusPick(publishState);
                                }}
                            />
                        )}
                        {publishState === UI_PUBLISH_STATES.PENDING && (
                            <Button
                                size="small"
                                variant="text"
                                disabled={isRejectPending || isLockedByOtherUser}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRejectClick && onRejectClick(article);
                                }}
                                sx={{
                                    minWidth: 'auto',
                                    px: 0.5,
                                    py: 0,
                                    textTransform: 'none',
                                    color: theme.palette.grey[700],
                                    opacity: 0.85,
                                    '&:hover': {
                                        opacity: 1,
                                        color: theme.palette.grey[900],
                                        backgroundColor: 'transparent',
                                    },
                                }}
                            >
                                {intl.formatMessage({ id: 'newsQueue.actions.reject' })}
                            </Button>
                        )}
                    </Box>

                    {article?.error_text && String(article?.publish_state ?? '').toLowerCase() === PUBLISH_STATES.ERROR && (
                        <Tooltip title={article.error_text} arrow>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                                {String(article?.publish_state ?? '').toLowerCase() === PUBLISH_STATES.ERROR ? (
                                    <ErrorIcon fontSize="small" color="error" />
                                ) : (
                                    <WarningIcon fontSize="small" sx={{ color: 'warning.main' }} />
                                )}
                            </Box>
                        </Tooltip>
                    )}

                </Box>

                {effectiveArticleIngress && (
                    <Box sx={{ gridColumn: 3, gridRow: 2, minWidth: 0 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                            {effectiveArticleIngress}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function areListArticlePropsEqual(prevProps, nextProps) {
    if (prevProps.article !== nextProps.article) return false;
    if (prevProps.isRejectPending !== nextProps.isRejectPending) return false;
    if ((prevProps.newlyInsertedAt ?? null) !== (nextProps.newlyInsertedAt ?? null)) return false;

    const prevLockBy = prevProps.lock?.locked_by ?? null;
    const nextLockBy = nextProps.lock?.locked_by ?? null;
    if (prevLockBy !== nextLockBy) return false;

    const prevLockExpires = prevProps.lock?.lock_expires_at ?? null;
    const nextLockExpires = nextProps.lock?.lock_expires_at ?? null;
    if (prevLockExpires !== nextLockExpires) return false;

    return true;
}

export default React.memo(ListArticle, areListArticlePropsEqual);
